/**
 * Complete service — ONE step that closes out the day (PROD-1279 D1). The
 * production record's counts (reimbursable / non-reimbursable / leftover per
 * DELIVERABLE × grade group — the same deliverable breakdown the Serve screen
 * uses) and the serving-line temperature are entered in a single table.
 * Submitting saves quantities + write-ins + temps and signs each mealtime's
 * record.
 *
 * Hosted by the Production Records page (the "Service records" task for a
 * whole service day — PROD-1277) and by the record detail page's catch-up
 * dialog, filtered to ONE production record. Serving in cafeteria mode is
 * optional: when it happened, `served` is pre-filled from the tallies;
 * otherwise the admin enters the counts here.
 *
 * The Grade COLUMN is gone: a deliverable's FIRST band is the legend under its
 * name and any further bands are sub-rows, because the temperature is measured
 * once per item while the counts are kept per band. That is exactly the shape
 * the shared `RecordEntryGrid` exists for — the same component the chef's
 * Temperature logs and the Transport logs task render. A service-line reading
 * the cafeteria logged pre-fills the input, marked beside it
 * (`CafeteriaModeTip`) instead of a banner over the step.
 *
 * A school that filled the printed record by hand can photograph it instead of
 * retyping it (PROD-1279 D25/D26): `SheetScanCard` sends up to six pages in one
 * request and `handleScan` pushes the numbers back through the SAME derivation
 * every keystroke goes through, so the two count identities still hold
 * afterwards. Cells the model was unsure about keep an amber `check` ring until
 * the worker touches them. Nothing is written to the server until the record is
 * signed.
 *
 * NOT A NATIVE `<form>` (PROD-1279 ruling E1, overriding `apps/ordo/CLAUDE.md`
 * § Forms for this file). A form's Enter-to-submit would SIGN a USDA
 * compliance record from any count box the moment the gates pass, and the
 * signature is prefilled with the worker's name. Every button here is an
 * explicit `type="button"` with an explicit `onClick`.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ordo/ui/dropdown-menu'
import { Input } from '@ordo/ui/input'
import { SearchableSelect } from '@ordo/ui/searchable-select'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Info, Plus, X } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import {
  type ProductionFillDeliverable,
  type ProductionFillGradeRow,
  type ProductionFillMealtime,
  type ProductionFillResponse,
  type ProductionWriteInKind,
  type RecentServiceMenuItem,
  useProductionFillQuery,
  useRecentServiceMenuItemsQuery,
  useSaveProductionQuantitiesMutation,
  useSaveProductionWriteInsMutation,
  useSaveServiceTemperaturesMutation,
  useSignProductionRecordMutation,
} from '@/api/cafeteria-production'
import { ApiError } from '@/api/http'
import { type ScanSheetResponse, useScanSheetMutation } from '@/api/sheet-scan'
import { CountInput } from '@/components/cafeteria/count-input'
import { CafeteriaWorkflowHeader } from '@/components/cafeteria/workflow-header'
import {
  RecordEntryCardHeader,
  type RecordEntryCell,
  type RecordEntryColumn,
  RecordEntryGrid,
  type RecordEntrySection,
} from '@/components/production/record-entry-grid'
import { ServiceCard } from '@/components/production/spreadsheet/record-step-cards'
import { type ScanApplySummary, SCHOOL_SHEET_SCAN_COPY, SheetScanCard } from '@/components/tasks/sheet-scan-card'
import { tempCheckRequired } from '@/lib/cafeteria/aggregate-deliveries'
import { cafeteriaDayViewState } from '@/lib/cafeteria/day-view'
import {
  distributeProductionFill,
  formatRelativeServiceDateLabel,
  MEALTIME_LABELS,
  type ProductionEdits,
  productionFillRowKey,
  type ProductionGradeEdit,
  type ProductionWriteInEdit,
  seedProductionEdits,
  serviceTempSaveEntries,
  signatoryNameFor,
  writeInRowIncomplete,
  writeInSaveEntries,
} from '@/lib/cafeteria/format'
import {
  blocksSubmit,
  type CountField,
  type CountViolation,
  countViolationMessage,
  deriveProductionCounts,
  type ProductionCounts,
  seedProductionCounts,
} from '@/lib/cafeteria/production-counts'
import { combineReadingInstant, defaultReadingTime, readingTimeFromInstant } from '@/lib/cafeteria/reading-time'
import {
  applyServiceScan,
  isScanAbort,
  parseTimeReading,
  remapPhotoIndices,
  SCAN_TIMEOUT_MESSAGE,
  type ScanCellFlag,
  type ServiceCountPatch,
} from '@/lib/sheet-scan/apply-result'
import { buildServiceScanRows, type ServiceScanTarget } from '@/lib/sheet-scan/build-request'
import { filesToImagePayloads } from '@/lib/sheet-scan/images'
import {
  overlayProductionEdits,
  productionDraftKey,
  useCafeteriaProductionDraftStore,
} from '@/stores/cafeteria-production-draft'
import { useProfile } from '@/stores/profile'

/**
 * The four count columns plus the serving-line reading. `inputLabel:
 * 'service temperature'` is deliberately unchanged from the two-step wizard so
 * `aria-label="<item> service temperature"` keeps naming the same control.
 */
const SERVICE_COLUMNS: ReadonlyArray<RecordEntryColumn> = [
  { key: 'served', header: 'Served', kind: 'count', width: '4.5rem', inputLabel: 'served' },
  { key: 'reimbursable', header: 'Reimbursable', kind: 'count', width: '7rem', inputLabel: 'reimbursable' },
  {
    key: 'nonReimbursable',
    header: 'Non-reimbursable',
    kind: 'count',
    width: '8.5rem',
    inputLabel: 'non-reimbursable',
    headerAdornment: (
      <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Staff, à la carte, second servings" />
    ),
  },
  { key: 'leftover', header: 'Leftovers', kind: 'count', width: '5.5rem', inputLabel: 'leftovers' },
  { key: 'service', header: 'Temp Serving', kind: 'temp', width: '12rem', inputLabel: 'service temperature' },
]

const COUNT_FIELDS = ['served', 'reimbursable', 'nonReimbursable', 'leftover'] as const

/**
 * The band a grade row stands for. This exact ladder used to fill the Grade
 * COLUMN; it is now the legend under the item name (first band) or a sub-row
 * legend (every further band).
 */
function gradeLegend(row: ProductionFillGradeRow): string {
  return row.groupLabel ?? row.gradeGroupName ?? row.rosterName ?? 'All grades'
}

/**
 * The `HH:MM` already stored against these records' SERVICE temperature rows,
 * or `''` when none of them carries one. Every mealtime a task signs shares one
 * reading time, so the first stored value is the task's.
 */
function storedServiceReadingTime(mealtimes: readonly ProductionFillMealtime[]): string {
  for (const mt of mealtimes) {
    const time = readingTimeFromInstant(mt.serviceReadingAt)
    if (time !== null) {
      return time
    }
  }
  return ''
}

/**
 * The `check`-flag set after a scan, keyed `${rowKey}:${field}` — the keys the
 * grid cells below look themselves up with. A count belongs to a grade BAND
 * (`productionFillRowKey`); the serving reading belongs to the DELIVERABLE,
 * because it is measured once across the bands.
 *
 * A re-read supersedes the previous one for every cell it FILLED, so a number
 * the model read confidently the second time loses its amber ring; a cell the
 * new photos never reached keeps whatever the last scan said about it.
 */
function nextServiceFlags(
  prev: ReadonlySet<string>,
  result: ScanSheetResponse,
  index: ReadonlyMap<string, ServiceScanTarget>,
  flags: readonly ScanCellFlag[],
): Set<string> {
  const next = new Set(prev)
  for (const row of result.rows) {
    const target = index.get(row.rowId)
    if (!target) {
      continue
    }
    for (const [field, cell] of Object.entries(row.cells)) {
      if (cell.value === null) {
        continue
      }
      if (field === 'servingTemperatureF') {
        // `applyServiceScan` ignores a temperature on a grade SUB-ROW, so one
        // must not clear the item's warning either — that would drop the ring
        // off a reading the re-shoot never actually re-read.
        if (target.isItemRow) {
          next.delete(`${target.deliverableKey}:${field}`)
        }
        continue
      }
      next.delete(`${target.rowKey}:${field}`)
    }
  }
  for (const flag of flags) {
    next.add(`${flag.rowKey}:${flag.field}`)
  }
  return next
}

export interface CompleteServiceFlowProps {
  locationId: string
  /** SERVICE axis — the day the meals were served. */
  serviceDate: string
  /** When set, only this production record's mealtime is shown and signed. */
  productionRecordId?: string | null
  /** Dialog-hosted: fill the container instead of the viewport. */
  embedded?: boolean
  /**
   * `amend`: the record is already signed. The same tables are shown, but the
   * worker must state why the record is changing; that reason, the edits and
   * the re-signature land on the record's append-only amendment trail. No
   * "Save for later" draft is read or written — a signed record has no
   * in-progress state to resume.
   */
  mode?: 'complete' | 'amend'
  /**
   * Second header line under "Service records". The Production Records host
   * names the campus and the service day here — the fullscreen dialog hides
   * the page, so the wizard has to say which record it is filling.
   */
  subtitle?: string
  /** "Save for later" — leave without signing (draft persists). */
  onExit: () => void
  /** Every (filtered) mealtime's record was saved + signed. */
  onComplete: () => void
}

export function CompleteServiceFlow({
  locationId,
  serviceDate,
  productionRecordId = null,
  embedded = false,
  mode = 'complete',
  subtitle,
  onExit,
  onComplete,
}: CompleteServiceFlowProps) {
  const amend = mode === 'amend'
  // The host's subtitle is "{campus} · {day}". The scan's context label wants
  // the campus alone — the date travels separately — and falls back to the id
  // when the flow is mounted without a subtitle (the record detail dialog).
  const campusLabel = subtitle?.split('·')[0]?.trim() || locationId
  const [amendReason, setAmendReason] = useState('')
  const fillQuery = useProductionFillQuery({ schoolLocationId: locationId, serviceDate })
  const fill = fillQuery.data

  const draftKey = productionDraftKey(locationId, serviceDate)
  const [edits, setEdits] = useState<ProductionEdits>({})
  // "Time reading" — ONE per task (the spec is literal), stamped on every
  // mealtime this signature closes. Seeded from the draft, else the current
  // clock when the record IS today and blank otherwise: a catch-up record must
  // not open pre-stamped with a time nobody took a reading at.
  const [readingTime, setReadingTimeState] = useState<string>(() => defaultReadingTime(serviceDate))
  const [submitting, setSubmitting] = useState(false)
  const [signatoryName, setSignatoryName] = useState('')
  // Signature prefill: the signed-in worker's display name, editable as
  // before. Only fills while untouched, so a name the worker typed (or
  // cleared) is never clobbered by a late-resolving profile.
  const { profile } = useProfile()
  const signatureTouchedRef = useRef(false)
  useEffect(() => {
    if (!signatureTouchedRef.current) {
      const prefill = signatoryNameFor(profile?.user)
      if (prefill !== '') {
        setSignatoryName((current) => (current === '' ? prefill : current))
      }
    }
  }, [profile])
  // Only persist once the worker actually edits a field (or a prior draft was
  // overlaid). Without this we'd write the freshly-seeded server values back as
  // a "draft" on every load, which could later overlay stale defaults on top of
  // newer server data on reopen.
  const dirtyRef = useRef(false)
  /**
   * `rowKey:field` pairs the school typed directly — the served-driven
   * auto-derivation must never overwrite an explicit entry.
   */
  const manualFieldsRef = useRef<Set<string>>(new Set())
  /**
   * `prId:rowKey` → the violations from that row's last edit. Warnings render
   * inline under the row; a submit-blocking one disables Continue, because the
   * server rejects `reimbursable + nonReimbursable > served` outright and the
   * worker would otherwise meet that only as a failed save at the very end.
   */
  const [rowViolations, setRowViolations] = useState<Record<string, CountViolation[]>>({})
  // `${rowKey}:${field}` for every cell a photo filled at LOW confidence. The
  // grid rings those amber; editing the cell clears its key, because the value
  // is then the worker's, not the model's.
  const [scanFlags, setScanFlags] = useState<Set<string>>(new Set())

  const scanMutation = useScanSheetMutation()
  const saveQuantities = useSaveProductionQuantitiesMutation()
  const saveWriteIns = useSaveProductionWriteInsMutation()
  const saveTemps = useSaveServiceTemperaturesMutation()
  const sign = useSignProductionRecordMutation()

  // The LEFTOVER write-in pick list: menu items on this school's production
  // records in the two weeks before this service date.
  const recentItemsQuery = useRecentServiceMenuItemsQuery({ schoolLocationId: locationId, serviceDate })
  const recentItems = recentItemsQuery.data?.items ?? []

  // The record filter applies once, here: the tables, the gates, and the
  // submit loop all iterate this list — other mealtimes on the same day are
  // untouched (and their drafts preserved) when the flow is record-scoped.
  const mealtimes = useMemo(() => {
    const all = fill?.mealtimes ?? []
    return productionRecordId ? all.filter((mt) => mt.productionRecordId === productionRecordId) : all
  }, [fill, productionRecordId])

  // Re-seed whenever TanStack Query delivers a structurally-new `fill`, not just
  // once. Structural sharing keeps the `fill` reference stable while the data is
  // unchanged, so focus refetches that return identical data don't clobber the
  // worker's in-progress edits — but a refetch that brings the freshly-saved
  // SERVICE temps (e.g. after signing, or when a stale cache snapshot is
  // replaced) re-seeds the inputs instead of latching onto the stale values.
  // The seed is the server-truth BASE; any persisted "Save for later" draft is
  // overlaid on top so the worker's in-progress entries survive navigation
  // while read-only server fields (e.g. served) stay current.
  const seededFillRef = useRef<ProductionFillResponse | null>(null)
  useEffect(() => {
    if (fill && fill.mealtimes.length > 0 && seededFillRef.current !== fill) {
      seededFillRef.current = fill
      const base = seedProductionEdits(fill)
      // An amendment starts from what was signed, never from a stale draft.
      const draft = amend ? {} : useCafeteriaProductionDraftStore.getState().getDraft(draftKey)
      // A persisted draft means there are prior edits to keep auto-saving.
      dirtyRef.current = Object.keys(draft).length > 0
      const merged = overlayProductionEdits(base, draft)
      setEdits(merged)
      // The time rides on the records, so it restores through the SAME overlay
      // as every other edit rather than off the raw draft — a re-seed that
      // dropped it would otherwise be invisible here and then be persisted
      // away by the next count the worker touches. Every record carries the
      // same value, so the first one is the task's.
      //
      // With no draft time (a fresh reopen, and ALWAYS when amending) the
      // record's own stored SERVICE reading is next: a signed record already
      // has a HACCP time, and re-signing it off the current clock would move a
      // compliance fact nobody re-measured. `defaultReadingTime` is the last
      // resort, and it is blank on any day but today.
      setReadingTimeState(
        Object.values(merged)[0]?.readingTime || storedServiceReadingTime(mealtimes) || defaultReadingTime(serviceDate),
      )
    }
  }, [amend, draftKey, fill, mealtimes, serviceDate])

  // Persist the draft on every edit (mirrors the serve surface), but only once
  // the worker has actually touched a field — never the bare seed.
  useEffect(() => {
    if (!amend && dirtyRef.current && Object.keys(edits).length > 0) {
      useCafeteriaProductionDraftStore.getState().saveDraft(draftKey, edits)
    }
  }, [amend, draftKey, edits])

  /**
   * One time for the whole task, mirrored onto every record so the draft
   * persistence (which is keyed by record) carries it without a second store.
   */
  function setReadingTime(next: string): void {
    dirtyRef.current = true
    setReadingTimeState(next)
    setEdits((prev) => {
      const merged: ProductionEdits = {}
      for (const [prId, pr] of Object.entries(prev)) {
        merged[prId] = { ...pr, readingTime: next }
      }
      return merged
    })
  }

  function updateRow(
    prId: string,
    rowKey: string,
    field: keyof ProductionGradeEdit,
    value: number,
    planned?: number,
  ): void {
    dirtyRef.current = true
    manualFieldsRef.current.add(`${rowKey}:${field}`)
    const manual = new Set<CountField>(
      (['served', 'reimbursable', 'nonReimbursable', 'leftover'] as const).filter((f) =>
        manualFieldsRef.current.has(`${rowKey}:${f}`),
      ),
    )
    // Derive ONCE, outside the state updater: an updater that calls another
    // setState is impure and would double-fire under StrictMode.
    const current = edits[prId]?.rows[rowKey] ?? { served: 0, reimbursable: 0, nonReimbursable: 0, leftover: 0 }
    // The two identities (served = reimbursable + non-reimbursable, and
    // served + leftover = planned) live in `production-counts.ts` so the wizard
    // never re-derives them inline. Violations are surfaced, not silently
    // corrected — see that module's header.
    const { counts, violations } = deriveProductionCounts({
      current,
      field,
      value,
      planned: planned ?? current.served + current.leftover,
      manual,
    })
    setRowViolations((prev) => ({ ...prev, [`${prId}:${rowKey}`]: violations }))
    setEdits((prev) => {
      const pr = prev[prId]
      // Spread the existing row: the unified input keeps its drafted Ordered /
      // Prepared / Received on the same row object, and this task must not
      // discard them. `deriveProductionCounts` already spreads `current`, so
      // this writer never dropped them; the spread keeps that true even if the
      // derivation stops doing so. `resetRow` below DID drop them.
      return pr
        ? { ...prev, [prId]: { ...pr, rows: { ...pr.rows, [rowKey]: { ...pr.rows[rowKey], ...counts } } } }
        : prev
    })
  }

  /**
   * Clear every manual mark for a row and re-seed from the provisioned count.
   * Without this, one mistyped number leaves the row permanently un-derived
   * with no way back short of reloading the page.
   */
  function resetRow(prId: string, rowKey: string, planned: number): void {
    dirtyRef.current = true
    for (const f of COUNT_FIELDS) {
      manualFieldsRef.current.delete(`${rowKey}:${f}`)
    }
    // The boxes now hold the provisioned numbers, so an amber "the photo was
    // hard to read" ring would be pointing at a value the model never produced.
    setScanFlags((prev) => {
      const next = new Set(prev)
      for (const f of COUNT_FIELDS) {
        next.delete(`${rowKey}:${f}`)
      }
      return next.size === prev.size ? prev : next
    })
    setRowViolations((prev) => ({ ...prev, [`${prId}:${rowKey}`]: [] }))
    setEdits((prev) => {
      const pr = prev[prId]
      return pr
        ? {
            ...prev,
            [prId]: { ...pr, rows: { ...pr.rows, [rowKey]: { ...pr.rows[rowKey], ...seedProductionCounts(planned) } } },
          }
        : prev
    })
  }

  function updateTemp(prId: string, deliverableKey: string, value: number | null): void {
    dirtyRef.current = true
    setEdits((prev) => {
      const pr = prev[prId]
      return pr ? { ...prev, [prId]: { ...pr, temps: { ...pr.temps, [deliverableKey]: value } } } : prev
    })
  }

  /**
   * Apply a whole scan's count patches in ONE pass, against the LATEST edits.
   *
   * `updateRow` derives from the `edits` of the render that created it, so
   * calling it once per patch would make every patch after the first derive
   * from the PRE-scan row and overwrite the ones before it (it replaces the row
   * wholesale). The identical derivation runs here over a local accumulator —
   * same `deriveProductionCounts`, same manual-field bookkeeping, same order —
   * and lands as a single `setEdits`.
   *
   * The derivation runs INSIDE the updater, not over the render-time `edits`: a
   * scan can take two minutes and the grid stays live throughout, so a count
   * typed meanwhile is already in state and must be what the patches build on.
   * The updater writes no state of its own — it only fills `violations`, which
   * the `setRowViolations` below (a later hook, so a later replay in the same
   * render) reads. Re-running it under StrictMode assigns the same values.
   */
  function applyScanCountPatches(patches: readonly ServiceCountPatch[]): void {
    if (patches.length === 0) {
      return
    }
    dirtyRef.current = true
    const violations: Record<string, CountViolation[]> = {}
    setEdits((prev) => {
      const working: Record<string, Record<string, ProductionCounts>> = {}
      for (const patch of patches) {
        manualFieldsRef.current.add(`${patch.rowKey}:${patch.field}`)
        const manual = new Set<CountField>(
          COUNT_FIELDS.filter((f) => manualFieldsRef.current.has(`${patch.rowKey}:${f}`)),
        )
        const current = working[patch.prId]?.[patch.rowKey] ??
          prev[patch.prId]?.rows[patch.rowKey] ?? { served: 0, reimbursable: 0, nonReimbursable: 0, leftover: 0 }
        const derived = deriveProductionCounts({
          current,
          field: patch.field,
          value: patch.value,
          planned: patch.planned,
          manual,
        })
        working[patch.prId] = { ...working[patch.prId], [patch.rowKey]: derived.counts }
        violations[`${patch.prId}:${patch.rowKey}`] = derived.violations
      }
      const next = { ...prev }
      for (const [prId, rows] of Object.entries(working)) {
        const pr = next[prId]
        if (pr) {
          next[prId] = { ...pr, rows: { ...pr.rows, ...rows } }
        }
      }
      return next
    })
    setRowViolations((prev) => ({ ...prev, ...violations }))
  }

  function flagFor(rowKey: string, field: string): 'check' | undefined {
    return scanFlags.has(`${rowKey}:${field}`) ? 'check' : undefined
  }

  /** The worker just typed here, so the model's doubt about this cell is moot. */
  function clearScanFlag(rowKey: string, field: string): void {
    const marked = `${rowKey}:${field}`
    setScanFlags((prev) => {
      if (!prev.has(marked)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(marked)
      return next
    })
  }

  /** The four count boxes for one grade row — the cells of a row or a sub-row. */
  function serviceCells(
    prId: string,
    deliverableKey: string,
    row: ProductionFillGradeRow,
  ): Record<string, RecordEntryCell> {
    const rowKey = productionFillRowKey(deliverableKey, row)
    const edit = edits[prId]?.rows[rowKey] ?? {
      served: row.served,
      reimbursable: row.reimbursable,
      nonReimbursable: row.nonReimbursable,
      leftover: row.leftover,
    }
    const cells: Record<string, RecordEntryCell> = {}
    for (const field of COUNT_FIELDS) {
      cells[field] = {
        kind: 'count',
        value: edit[field],
        flag: flagFor(rowKey, field),
        onValueChange: (next: string) => {
          clearScanFlag(rowKey, field)
          updateRow(prId, rowKey, field, Number(next), row.planned)
        },
      }
    }
    return cells
  }

  /**
   * A row's warnings plus its Reset link, rendered full width under the line
   * instead of hanging inside the (narrow) served column.
   */
  function violationsNode(prId: string, deliverableKey: string, row: ProductionFillGradeRow): ReactNode {
    const rowKey = productionFillRowKey(deliverableKey, row)
    const violations = rowViolations[`${prId}:${rowKey}`] ?? []
    if (violations.length === 0) {
      return null
    }
    return (
      <div className="mt-2 space-y-1">
        {violations.map((violation) => (
          <p
            key={violation.kind}
            className={cn('text-xs', blocksSubmit(violation) ? 'text-destructive' : 'text-amber-600')}>
            {countViolationMessage(violation)}
          </p>
        ))}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => resetRow(prId, rowKey, row.planned)}>
          Reset row
        </Button>
      </div>
    )
  }

  function addWriteIn(prId: string, kind: ProductionWriteInKind): void {
    dirtyRef.current = true
    const row: ProductionWriteInEdit = {
      id: null,
      key: crypto.randomUUID(),
      kind,
      menuItemId: null,
      menuItemName: null,
      description: '',
      quantity: 0,
      reimbursableQty: 0,
      nonReimbursableQty: 0,
      reimbursableTouched: false,
    }
    setEdits((prev) => {
      const pr = prev[prId]
      return pr ? { ...prev, [prId]: { ...pr, writeIns: [...pr.writeIns, row] } } : prev
    })
  }

  function updateWriteIn(prId: string, key: string, patch: Partial<ProductionWriteInEdit>): void {
    dirtyRef.current = true
    setEdits((prev) => {
      const pr = prev[prId]
      if (!pr) {
        return prev
      }
      const writeIns = pr.writeIns.map((w) => {
        if (w.key !== key) {
          return w
        }
        const next = { ...w, ...patch }
        // The claim split obeys `reimbursable + nonReimbursable = quantity`:
        // whichever side the worker edits, the other absorbs the remainder —
        // the same interlock the main quantity table uses.
        if ('reimbursableQty' in patch) {
          next.reimbursableTouched = true
          next.nonReimbursableQty = Math.max(0, next.quantity - next.reimbursableQty)
        } else if ('nonReimbursableQty' in patch) {
          next.reimbursableTouched = true
          next.reimbursableQty = Math.max(0, next.quantity - next.nonReimbursableQty)
        } else if ('quantity' in patch && next.kind !== 'DROPPED') {
          if (next.reimbursableTouched) {
            // Preserve the asserted non-reimbursable count; reimbursable
            // absorbs the change (first servings are the derived default).
            next.nonReimbursableQty = Math.min(next.nonReimbursableQty, next.quantity)
            next.reimbursableQty = Math.max(0, next.quantity - next.nonReimbursableQty)
          } else {
            // Until the worker asserts a split, every write-in meal is a
            // first serving — reimbursable tracks quantity.
            next.reimbursableQty = next.quantity
            next.nonReimbursableQty = 0
          }
        }
        return next
      })
      return { ...prev, [prId]: { ...pr, writeIns } }
    })
  }

  function removeWriteIn(prId: string, key: string): void {
    dirtyRef.current = true
    setEdits((prev) => {
      const pr = prev[prId]
      return pr ? { ...prev, [prId]: { ...pr, writeIns: pr.writeIns.filter((w) => w.key !== key) } } : prev
    })
  }

  /**
   * Cells a sheet photo could fill: four counts per grade band, plus one
   * reading per deliverable that takes one. The denominator in the card's
   * "Filled X of Y fields".
   */
  const scanFieldTotal = mealtimes.reduce(
    (total, mt) =>
      total +
      mt.deliverables.reduce(
        (sum, d) => sum + d.gradeRows.length * COUNT_FIELDS.length + (tempCheckRequired(d.temperatureClass) ? 1 : 0),
        0,
      ),
    0,
  )

  /**
   * Read the printed production record and top the grid up (D26, plan 03 §E.1).
   * Counts go through the same derivation a keystroke does; the serving reading
   * goes through `updateTemp`. Nothing is submitted: the worker still signs.
   */
  async function handleScan(files: File[]): Promise<ScanApplySummary> {
    const prepared = await filesToImagePayloads(files)
    if (prepared.payloads.length === 0) {
      // Every photo was refused before it left the device (wrong format, too
      // big): name the first reason rather than a generic failure.
      throw new Error(prepared.errors[0] ?? SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }
    const { rows, index } = buildServiceScanRows(mealtimes)
    let response: ScanSheetResponse
    try {
      response = await scanMutation.mutateAsync({
        schoolLocationId: locationId,
        serviceDate,
        task: 'service',
        contextLabel: campusLabel,
        serviceDateLabel: formatRelativeServiceDateLabel(serviceDate),
        images: prepared.payloads,
        rows,
      })
    } catch (err) {
      if (isScanAbort(err)) {
        throw new Error(SCAN_TIMEOUT_MESSAGE, { cause: err })
      }
      throw err
    }
    // The server numbers its photos by what it RECEIVED; put the worker's own
    // page numbers back before anything phrases a "Photo N" warning.
    const result = remapPhotoIndices(response, prepared.sourceIndices)
    const applied = applyServiceScan(result, index, edits)
    // A deliverable with no grade bands has no count boxes, and its target
    // carries an empty row key — writing one would plant a phantom row in the
    // edits (and in the persisted draft) that nothing ever reads.
    applyScanCountPatches(applied.patches.filter((patch) => patch.rowKey !== ''))
    for (const patch of applied.temps) {
      updateTemp(patch.prId, patch.deliverableKey, patch.value)
    }
    setScanFlags((prev) => nextServiceFlags(prev, result, index, applied.flags))
    // The sheet's own time box, but never over a time already on screen.
    const scannedTime = parseTimeReading(result.timeReadingText)
    const timeReadingApplied = scannedTime !== null && readingTime === ''
    if (scannedTime !== null && timeReadingApplied) {
      setReadingTime(scannedTime)
    }
    return {
      ...applied.summary,
      // A photo refused on the device is as much a "check this" as a photo the
      // model refused, so both kinds of line reach the card together.
      warnings: [...prepared.errors, ...applied.summary.warnings],
      filledCount: Math.min(applied.summary.filledCount, scanFieldTotal),
      totalCount: scanFieldTotal,
      timeReadingApplied,
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!fill) {
      return
    }
    setSubmitting(true)
    const reason = amend ? amendReason.trim() : null
    try {
      for (const mt of mealtimes) {
        const prEdits = edits[mt.productionRecordId]
        if (!prEdits) {
          continue
        }
        // Merge the worker's edits onto each deliverable's grade rows (served is
        // read-only), then collapse age groups → PR line × tier buckets.
        const lines = mt.deliverables.flatMap((deliverable) =>
          distributeProductionFill({
            lineTargets: deliverable.lineTargets,
            gradeRows: deliverable.gradeRows.map((row) => {
              const edit = prEdits.rows[productionFillRowKey(deliverable.deliverableKey, row)]
              return {
                ...row,
                served: edit?.served ?? row.served,
                reimbursable: edit?.reimbursable ?? row.reimbursable,
                nonReimbursable: edit?.nonReimbursable ?? row.nonReimbursable,
                leftover: edit?.leftover ?? row.leftover,
              }
            }),
          }),
        )
        await saveQuantities.mutateAsync({ productionRecordId: mt.productionRecordId, lines, reason })
        // Footer write-ins: the full desired set — the server soft-deletes
        // rows the worker removed, so this posts even when the list is empty.
        await saveWriteIns.mutateAsync({
          productionRecordId: mt.productionRecordId,
          writeIns: writeInSaveEntries(prEdits.writeIns),
          reason,
        })
        // Per-deliverable service temps: fan each reading out to its constituent
        // bundle deliverables. Skip deliverables with no value entered/pre-filled.
        const temperatures = mt.deliverables.flatMap((deliverable) => {
          if (!tempCheckRequired(deliverable.temperatureClass)) {
            // Shelf-stable: no reading exists, and persisting one would be a
            // fabricated entry on a compliance record.
            return []
          }
          const value = prEdits.temps[deliverable.deliverableKey]
          return value == null || Number.isNaN(value) ? [] : serviceTempSaveEntries(deliverable, value)
        })
        if (temperatures.length > 0) {
          await saveTemps.mutateAsync({
            productionRecordId: mt.productionRecordId,
            temperatures,
            // Null when the box is blank, and the fetcher then omits the key
            // entirely — the body stays exactly what it is today.
            readingAt: combineReadingInstant(serviceDate, readingTime),
          })
        }
        await sign.mutateAsync({
          productionRecordId: mt.productionRecordId,
          signedByName: signatoryName.trim(),
          reason,
        })
      }
      // Record-scoped runs drop only the signed record's draft entries —
      // another mealtime's in-progress "Save for later" draft must survive.
      const store = useCafeteriaProductionDraftStore.getState()
      if (productionRecordId) {
        const rest = { ...store.getDraft(draftKey) }
        delete rest[productionRecordId]
        if (Object.keys(rest).length > 0) {
          store.saveDraft(draftKey, rest)
        } else {
          store.clearDraft(draftKey)
        }
      } else {
        store.clearDraft(draftKey)
      }
      toast.success(
        amend
          ? 'Production record amended.'
          : mealtimes.length === 1
            ? 'Production record signed.'
            : 'Production records signed.',
      )
      onComplete()
    } catch (err) {
      // Four writes run per mealtime (quantities, write-ins, temps, sign) and
      // the server names its refusal (`{ error, message }`: no service
      // temperature on the record, an unknown record, no access to the
      // campus). Show that message: the fixed quantities hint hid a
      // temperature refusal in the 2026-09-14 review demo, and the worker
      // cannot fix what the toast does not name. The fixed line stays for
      // non-API failures (offline, a thrown client error), which carry no
      // message a worker could act on.
      toast.error(
        err instanceof ApiError
          ? err.message
          : amend
            ? 'Could not amend the record. Please try again.'
            : 'Could not complete service. Check the entered quantities and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Write surface — quantities/temps are edited against `fill`'s row ids, so
  // the previous day's placeholder must never render. Same rule as the
  // Confirm-deliveries wizard.
  const { showSkeleton: loading } = cafeteriaDayViewState(
    {
      isPending: fillQuery.isPending,
      isPlaceholderData: fillQuery.isPlaceholderData,
      hasData: Boolean(fillQuery.data),
    },
    { allowStale: false },
  )

  // Every deliverable across every mealtime needs a service temperature before
  // the day can be signed (mirrors Confirm-deliveries' all-temps gate).
  const allTempsSet =
    mealtimes.length > 0 &&
    mealtimes.every((mt) => {
      const prEdits = edits[mt.productionRecordId]
      return (
        prEdits != null &&
        mt.deliverables
          .filter((d) => tempCheckRequired(d.temperatureClass))
          .every((d) => prEdits.temps[d.deliverableKey] != null)
      )
    })

  // A row whose split contradicts its served count would be rejected by the
  // server (`invalid_quantities`), so hold the worker at the quantities step
  // rather than let them discover it as a failed save after signing.
  const hasBlockingViolation = Object.values(rowViolations).some((list) => list.some(blocksSubmit))

  // An incomplete write-in (no quantity, or missing its item/description)
  // would be rejected by the server — and silently dropping it would erase a
  // count the worker added to a compliance record. Hold at the step instead.
  const hasIncompleteWriteIn = mealtimes.some((mt) =>
    (edits[mt.productionRecordId]?.writeIns ?? []).some(writeInRowIncomplete),
  )

  // The time, the signature and (when amending) the reason are the NEXT CARD
  // on the canvas under the table — the same `Service` card the production
  // record input renders — followed by the one primary. Never a bar pinned
  // under the scroll container: it floated its fields off the grid and left
  // no room beneath them (Abner, 2026-10-09).
  const signGateOk =
    !submitting &&
    allTempsSet &&
    signatoryName.trim() !== '' &&
    (!amend || amendReason.trim() !== '') &&
    !hasBlockingViolation &&
    !hasIncompleteWriteIn
  const signCards =
    !loading && mealtimes.length > 0 ? (
      <>
        <div className="mt-6">
          <ServiceCard
            signature={signatoryName}
            onSignatureChange={(next) => {
              signatureTouchedRef.current = true
              setSignatoryName(next)
            }}
            time={readingTime}
            onTimeChange={setReadingTime}
            signed={null}
            disabled={submitting}
            amendment={amend ? { reason: amendReason, onReasonChange: setAmendReason } : null}
          />
        </div>
        <div className="mt-8 flex flex-col gap-2 md:flex-row md:justify-end">
          <Button type="button" className="w-full md:w-auto" onClick={() => void handleSubmit()} disabled={!signGateOk}>
            {submitting ? 'Signing…' : amend ? 'Sign amendment' : 'Sign & complete'}
          </Button>
        </div>
      </>
    ) : null

  // One section per mealtime; one row per deliverable. The deliverable's FIRST
  // grade band is the legend under its name and fills the row's own count
  // boxes; every further band is a sub-row with its own boxes. The temperature
  // belongs to the row, so it is measured once and spans the bands.
  const sections: RecordEntrySection[] = mealtimes.map((mt) => ({
    key: mt.productionRecordId,
    header: <RecordEntryCardHeader label={MEALTIME_LABELS[mt.mealtime]} />,
    emptyText: 'No items on this record.',
    footer: (
      <WriteInsFooter
        writeIns={edits[mt.productionRecordId]?.writeIns ?? []}
        recentItems={recentItems}
        dayItems={dayItemOptions(mt.deliverables)}
        onAdd={(kind) => addWriteIn(mt.productionRecordId, kind)}
        onUpdate={(key, patch) => updateWriteIn(mt.productionRecordId, key, patch)}
        onRemove={(key) => removeWriteIn(mt.productionRecordId, key)}
      />
    ),
    rows: mt.deliverables.map((deliverable) => {
      const [first, ...rest] = deliverable.gradeRows
      return {
        key: deliverable.deliverableKey,
        label: deliverable.label,
        legend: first ? gradeLegend(first) : null,
        temperatureClass: deliverable.temperatureClass,
        cells: {
          ...(first ? serviceCells(mt.productionRecordId, deliverable.deliverableKey, first) : {}),
          service: {
            kind: 'temp' as const,
            offered: tempCheckRequired(deliverable.temperatureClass),
            value: edits[mt.productionRecordId]?.temps[deliverable.deliverableKey] ?? null,
            flag: flagFor(deliverable.deliverableKey, 'servingTemperatureF'),
            onChange: (next: number | null) => {
              clearScanFlag(deliverable.deliverableKey, 'servingTemperatureF')
              updateTemp(mt.productionRecordId, deliverable.deliverableKey, next)
            },
            reading: deliverable.serviceTemperature.cafeteria,
            submitted: null,
          },
        },
        footer: first ? violationsNode(mt.productionRecordId, deliverable.deliverableKey, first) : null,
        subRows: rest.map((row) => ({
          key: productionFillRowKey(deliverable.deliverableKey, row),
          legend: gradeLegend(row),
          cells: serviceCells(mt.productionRecordId, deliverable.deliverableKey, row),
          footer: violationsNode(mt.productionRecordId, deliverable.deliverableKey, row),
        })),
      }
    }),
  }))

  return (
    <div
      className={embedded ? 'flex h-full min-h-0 flex-1 flex-col bg-background' : 'flex h-dvh flex-col bg-background'}>
      <CafeteriaWorkflowHeader
        title={amend ? 'Amend production record' : 'Service records'}
        subtitle={subtitle}
        actions={
          <Button type="button" variant="outline" onClick={onExit}>
            {amend ? 'Cancel' : 'Save for later'}
          </Button>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto" data-record-canvas>
        {/* max-w-5xl: five columns plus the item name need the room. */}
        <div className="mx-auto w-full min-w-0 max-w-5xl px-4 py-6 md:px-6 md:py-8">
          <h2 className="text-2xl font-semibold tracking-normal">Production records</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {amend
              ? 'This record is already signed. Correct the counts below; every change is recorded on the amendment trail and the record is re-signed.'
              : 'Enter how many of the served items were reimbursable, non-reimbursable, and leftovers, and the serving-line temperature (°F) for each item.'}
          </p>

          {/* Above the table, below the heading block, full width — the chef
              precedent. Only where there is something to fill. */}
          {!loading && mealtimes.length > 0 ? (
            <div className="mt-6">
              <SheetScanCard
                copy={SCHOOL_SHEET_SCAN_COPY}
                multiple
                maxPhotos={6}
                onExtract={handleScan}
                disabled={submitting}
              />
            </div>
          ) : null}

          {loading ? (
            <div className="mt-6 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          ) : mealtimes.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
              No production records have been generated for this day yet.
            </div>
          ) : (
            <div className="mt-6">
              <RecordEntryGrid columns={SERVICE_COLUMNS} sections={sections} />
            </div>
          )}
          {signCards}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Write-ins footer — additional foods & dropped meals
// ---------------------------------------------------------------------------

const WRITE_IN_KIND_LABEL: Record<ProductionWriteInKind, string> = {
  LEFTOVER: 'Re-served leftover',
  BACKUP: 'Backup meal',
  DROPPED: 'Unservable',
}

/** One pickable day-item for an unservable row: the mealtime's own meals. */
export interface DayItemOption {
  label: string
  menuItemId: string | null
}

/**
 * The mealtime's own items as the unservable pick list, deduped by label.
 * Catalog identity rides along from the first line target that carries one
 * (bundle packages resolve their parent cell's menu item).
 */
export function dayItemOptions(deliverables: ProductionFillDeliverable[]): DayItemOption[] {
  const byLabel = new Map<string, DayItemOption>()
  for (const deliverable of deliverables) {
    if (!byLabel.has(deliverable.label)) {
      byLabel.set(deliverable.label, {
        label: deliverable.label,
        menuItemId: deliverable.lineTargets.find((t) => t.menuItemId)?.menuItemId ?? null,
      })
    }
  }
  return [...byLabel.values()]
}

/**
 * How the footer sits on its surface. `footer` (default) is the shipped look
 * under a quantity table: a tinted band with an eyebrow title. `card` is the
 * unified record input's stand-alone section (PROD-1279, image-18): the host
 * draws the border, and the title is a real section title one step above the
 * body, so the block reads as its own section rather than the table's tail.
 */
export type WriteInsFooterVariant = 'footer' | 'card'

const WRITE_INS_STYLES: Record<WriteInsFooterVariant, { root: string; title: string; body: string; gap: string }> = {
  footer: {
    root: 'border-t bg-muted/20 px-4 py-3',
    title: 'text-xs font-semibold uppercase tracking-wide text-muted-foreground',
    body: 'mt-0.5 text-xs text-muted-foreground',
    gap: 'mt-3',
  },
  card: {
    root: 'p-5',
    title: 'text-base font-semibold leading-tight',
    body: 'mt-1 max-w-3xl text-sm text-muted-foreground',
    gap: 'mt-4',
  },
}

/**
 * Footer section under each mealtime's quantity table: meals served or lost
 * OUTSIDE the shipment-derived rows above — re-served leftovers from a recent
 * service, off-menu backup meals from on-hand stock, and unservable meals
 * (dropped/damaged, picked from the day's own items). The digital equivalent
 * of the hand-written rows at the bottom of a paper production record.
 * Served write-ins count toward the claim; unservable meals record product
 * loss only.
 */
export function WriteInsFooter({
  writeIns,
  recentItems,
  dayItems,
  onAdd,
  onUpdate,
  onRemove,
  variant = 'footer',
}: {
  writeIns: ProductionWriteInEdit[]
  recentItems: RecentServiceMenuItem[]
  /** The mealtime's own items — the pick list for unservable (DROPPED) rows. */
  dayItems: DayItemOption[]
  onAdd: (kind: ProductionWriteInKind) => void
  onUpdate: (key: string, patch: Partial<ProductionWriteInEdit>) => void
  onRemove: (key: string) => void
  variant?: WriteInsFooterVariant
}) {
  const styles = WRITE_INS_STYLES[variant]
  const leftoverOptions = recentItems.map((item) => ({
    value: item.menuItemId,
    label: item.name,
    description: `Served ${item.lastServiceDate}`,
  }))
  const dayOptions = dayItems.map((item) => ({ value: item.label, label: item.label }))
  return (
    <div className={styles.root}>
      <h3 className={styles.title}>Write-ins &amp; unservable meals</h3>
      <p className={styles.body}>
        Served more than what&rsquo;s listed above? Add the leftovers or backup meals you served. They count toward the
        claim. Unservable meals (dropped, damaged, spoiled) record product loss.
      </p>
      {writeIns.length > 0 ? (
        <ul className={cn(styles.gap, 'space-y-2')}>
          {writeIns.map((w) => {
            const incomplete = writeInRowIncomplete(w)
            return (
              <li key={w.key} className="rounded-md border bg-background p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex min-w-56 flex-1 flex-col gap-1">
                    <span className="text-xs text-muted-foreground">{WRITE_IN_KIND_LABEL[w.kind]}</span>
                    {w.kind === 'LEFTOVER' ? (
                      <SearchableSelect
                        value={w.menuItemId}
                        onChange={(menuItemId) =>
                          onUpdate(w.key, {
                            menuItemId,
                            menuItemName: leftoverOptions.find((o) => o.value === menuItemId)?.label ?? null,
                          })
                        }
                        options={leftoverOptions}
                        placeholder="Search the last two weeks of service…"
                      />
                    ) : w.kind === 'DROPPED' ? (
                      // Unservable rows pick from the day's own meals — the
                      // description is the picked label (what prints), the
                      // catalog id rides along when the item carries one. A
                      // previously-saved label that's no longer on today's
                      // list still displays via labelOverrides.
                      <SearchableSelect
                        value={w.description || null}
                        onChange={(label) =>
                          onUpdate(w.key, {
                            description: label ?? '',
                            menuItemId: dayItems.find((item) => item.label === label)?.menuItemId ?? null,
                          })
                        }
                        options={dayOptions}
                        labelOverrides={w.description ? { [w.description]: w.description } : undefined}
                        placeholder="Search today's meals…"
                      />
                    ) : (
                      <Input
                        value={w.description}
                        onChange={(e) => onUpdate(w.key, { description: e.target.value })}
                        placeholder="Meal + components, e.g. cereal, milk, fruit"
                      />
                    )}
                  </div>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">
                      {w.kind === 'DROPPED' ? 'Unservable' : 'Served'}
                    </span>
                    <CountInput
                      className="w-24"
                      value={w.quantity}
                      onValueChange={(next) => onUpdate(w.key, { quantity: Number(next) })}
                    />
                  </label>
                  {w.kind !== 'DROPPED' ? (
                    <>
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Reimbursable</span>
                        <CountInput
                          className="w-24"
                          value={w.reimbursableQty}
                          onValueChange={(next) =>
                            onUpdate(w.key, { reimbursableQty: Math.min(w.quantity, Number(next)) })
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Non-reimbursable</span>
                        <CountInput
                          className="w-24"
                          value={w.nonReimbursableQty}
                          onValueChange={(next) =>
                            onUpdate(w.key, { nonReimbursableQty: Math.min(w.quantity, Number(next)) })
                          }
                        />
                      </label>
                    </>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove write-in"
                    onClick={() => onRemove(w.key)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {incomplete ? (
                  <p className="mt-2 text-xs text-destructive">
                    {w.kind === 'LEFTOVER'
                      ? 'Pick the item the leftovers came from and enter how many were served.'
                      : w.kind === 'BACKUP'
                        ? 'Describe the meal (including its components) and enter how many were served.'
                        : 'Pick the meal that couldn’t be served and enter how many.'}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      <div className={cn(styles.gap, 'flex flex-wrap gap-2')}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              <Plus className="mr-1.5 h-4 w-4" />
              Add custom item
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => onAdd('LEFTOVER')}>Leftover from a recent service</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onAdd('BACKUP')}>Custom backup meal</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="sm" onClick={() => onAdd('DROPPED')}>
          <Plus className="mr-1.5 h-4 w-4" />
          Log unservable meals
        </Button>
      </div>
    </div>
  )
}
